import { useEffect, useState } from 'react';
import { WebinarStatus } from '@core/types';
import { WebinarDateTime } from '../WebinarDateTime';
import { CalendarAlt, DescriptionItem, DurationIcon } from '@core/ui';
import { DateTime, Duration, Interval } from 'luxon';
import { getI18n, useTranslation } from 'react-i18next';

export const ListWebinarCardDate = ({
    status,
    broadcastDateTime: broadcastDateTimeStr,
    startedTime,
    webinarDuration,
    isCancelled
}: {
    status: WebinarStatus;
    broadcastDateTime: string;
    startedTime?: string;
    webinarDuration: string;
    isCancelled: boolean;
    }) => {
    const { t } = useTranslation();
    const [now, setNow] = useState(DateTime.now());

    useEffect(() => {
        // Webinars that are not planned or started do not need to be updated
        if (status !== WebinarStatus.Planned && status !== WebinarStatus.Started) return;

        setNow(DateTime.now());
    }, [status]);

    if (!broadcastDateTimeStr) return <></>;

    const broadcastDateTime = DateTime.fromISO(broadcastDateTimeStr).toLocal().setLocale(getI18n().language);
    switch (status) {
        case WebinarStatus.Ended: {
            const datetime = DateTime.fromISO(startedTime ?? broadcastDateTimeStr).toLocal().setLocale(getI18n().language);
            const actualDuration = Duration.fromISOTime(webinarDuration);
            
            return (
                <dl className="webinar-item-datetime">
                    <DescriptionItem icon={<CalendarAlt />} label={t('WebinarDetails.Label.Date')} hideLabel>
                        <time dateTime={datetime.toFormat('yyyy-LL-dd')}>{datetime.toLocaleString(DateTime.DATE_MED)}</time>
                    </DescriptionItem>
                    <DescriptionItem icon={<DurationIcon />} label={t('WebinarDetails.Label.Duration')} hideLabel>
                        <time dateTime={actualDuration.toFormat("h'h' m'm'")}>{actualDuration.toFormat('h:mm')}</time>
                    </DescriptionItem>
                </dl>
            );
        }
        case WebinarStatus.Started: {
            const plannedDuration = Duration.fromISOTime(webinarDuration);

            if (!startedTime || now < broadcastDateTime) {
                return (
                    <WebinarDateTime broadcastDateTime={broadcastDateTime} plannedDuration={plannedDuration} isCancelled={isCancelled} />
                );
            }
            const interval = Interval.fromDateTimes(DateTime.fromISO(startedTime).toLocal().setLocale(getI18n().language), now);
            return <>{`${interval.toDuration().toFormat('hh:mm:ss')}`}
                <div className="flex items-center border-2 border-warning rounded-full text-warning uppercase -my-0.5 -ms-1 me-2 px-2 pe-2.5">
                    <span className="relative flex size-2 me-2">
                        <span className="animate-ping absolute h-full w-full inline-flex rounded-full bg-warning opacity-75"></span>
                        <span className="relative size-2 inline-flex rounded-full bg-warning"></span>
                    </span>
                    {t('Webinar.Live')}
                </div>
            </>;
        }
        case WebinarStatus.Planned: {
            const plannedDuration = Duration.fromISOTime(webinarDuration);

            if (now > broadcastDateTime) {
                return (<span className="animate-pulse block uppercase mb-4">{t('Webinar.DateTime.StartNow')}</span>);
            }
            const interval = Interval.fromDateTimes(now, broadcastDateTime);
            if (interval.length('hours') > 2) {
                return (
                    <WebinarDateTime broadcastDateTime={broadcastDateTime} plannedDuration={plannedDuration} isCancelled={isCancelled} />
                );
            }
            return (<span className="block uppercase mb-4">{t('Webinar.DateTime.StartIn')} {interval.toDuration().toFormat('hh:mm:ss')}</span>);
        }
        default: {
            const plannedDuration = Duration.fromISOTime(webinarDuration);

            return (
                <WebinarDateTime broadcastDateTime={broadcastDateTime} plannedDuration={plannedDuration} isCancelled={isCancelled} />
            );
        }
    }
};
